import { ApplicationError } from './applicationError.mjs';
import formatAddress from './formatAddress.mjs';
import normalizeQuery from './normalizeQuery.mjs';

export function makeAddressSearchModel({ searchAdapter, eventBus } = {}) {
    if (searchAdapter === undefined) {
        throw new ApplicationError('missing "searchAdapter" argument');
    }
    if (eventBus === undefined) {
        throw new ApplicationError('missing "eventBus" argument');
    }
    let currentQuery = null;
    return {
        async search({ query, city, country, limit = 5 } = {}) {
            if (query === undefined) {
                throw new ApplicationError('missing query');
            }
            if (city === undefined) {
                throw new ApplicationError('missing city');
            }
            if (country === undefined) {
                throw new ApplicationError('missing country');
            }
            if (isQueryTooShort(query)) {
                this.skipSearch(query);
            }
            else {
                await this.doSearch({ query, city, country, limit });
            }
        },

        skipSearch(query) {
            eventBus.publish('address:results', {
                addresses : [],
                queryRaw  : query,
                error     : new ApplicationError('query string too short'),
            });
        },

        async doSearch({ query, city, country, limit } = {}) {
            currentQuery = normalizeQuery(query, city, country);
            const result = await searchAdapter.searchAddresses({
                query : currentQuery,
                limit,
            });
            // console.log(result);
            if (result.query !== currentQuery) {
                return;
            }
            this.processResult({ query, city, country, result });
        },

        processResult({ query, city, country, result } = {}) {
            const addresses1 = filterByCityCountry(result.addresses, city, country);
            const addresses2 = removeDuplicates(addresses1);
            const { EN, BG } = splitByLanguage(addresses2);
            const primary  = hasBulgarianScript(query) ? BG : EN;
            const fallback =            primary === BG ? EN : BG;
            eventBus.publish('address:results', {
                queryRaw        : query,
                queryNormalized : currentQuery,
                addresses       : primary.length ? primary : fallback,
            });
        },
    };
};

const isQueryTooShort = query => query.trim().length < 3;

function filterByCityCountry(addresses, city, country) {
    return addresses.filter(
        adr => adr.city === city && adr.country === country
    );
}

function removeDuplicates(addresses) {
    const result = [];
    const seen = new Map();
    for (const adr of addresses) {
        const key = formatAddress(adr);
        if (!seen.has(key)) {
            result.push(adr);
            seen.set(key, adr);
        }
    }
    return result;
}

function splitByLanguage(addresses) {
    const EN = [], BG = [];
    for (const adr of addresses) {
        if (isLatinScript(adr.street)) {
            EN.push(adr);
        }
        else {
            BG.push(adr);
        }
    }
    return { EN, BG };
}

function lettersMatch(regex) {
    return input => {
        // Remove everything that's not a Unicode letter.
        const letters = input.replace(/[^\p{L}]/gu, '');
        if (!letters) {
            return true;
        }
        return regex.test(letters);
    };
}

function allLettersMatch(regexString) {
    return lettersMatch(new RegExp('^' + regexString + '$', 'u'));
}

function someLettersMatch(regexString) {
    return lettersMatch(new RegExp(regexString, 'u'));
}

const latinScriptRegexString = '\\p{Script=Latin}+';

const bulgarianScriptRegexString
    = '['
    + 'АБВГДЕЖЗИЙКЛМНОПРСТУФХЦЧШЩЪЬЮЯ'
    + 'абвгдежзийклмнопрстуфхцчшщъьюя'
    + '\u040D\u045D'  // Ѝ (U+040D) and ѝ (U+045D)
    + ']+';

export const isLatinScript  =  allLettersMatch(latinScriptRegexString);
export const hasLatinScript = someLettersMatch(latinScriptRegexString);

export const isBulgarianScript  =  allLettersMatch(bulgarianScriptRegexString);
export const hasBulgarianScript = someLettersMatch(bulgarianScriptRegexString);
